import { AppMenu } from "../components/AppMenu";
import { env } from "@/app/config/env";
import { ProfileButton } from "@/features/auth";
import { roleLabel } from "@/shared/lib/role-label";
import { Link, Navigate } from "react-router-dom";
import { ShieldCheck, User, Video } from "lucide-react";

import {
  useAuth,
  resolveAuthDestination,
  resolvePortals,
} from "@/features/auth";
import { Button } from "@/shared/components/ui/button";
import { CardContainer } from "@/shared/components/CardContainer";
import { MobileHeader } from "@/shared/components/MobileHeader";
import { ResponsiveViewWrapper } from "@/shared/components/ResponsiveViewWrapper";

/**
 * Central application dashboard displaying auth status and system capabilities.
 * If the user is unauthenticated, automatically redirects to the `/login` screen.
 */
export function HomePage() {
  const { isAuthenticated, user, activeTenant, roles, isTeacher, isStudent } =
    useAuth();

  // Auto-redirect to login screen if the user is unauthenticated
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  // Any role that owns a portal lands in it (or in the portal chooser when several apply). The
  // guard is on "has any portal", not on a specific role, so an Admin-only account is routed just
  // like a Teacher — and an account with no portal at all stays on this page instead of being
  // redirected to the path it is already on, which would loop.
  if (resolvePortals(roles).length > 0) {
    return <Navigate to={resolveAuthDestination(roles)} replace />;
  }

  return (
    <ResponsiveViewWrapper
      maxMobileWidth={430}
      className="bg-frame-surface"
      scrollable={false}
      aria-label="صفحه اصلی کایرسافت"
    >
      <MobileHeader
        leadingAction={<ProfileButton />}
        title="کایرسافت"
        subtitle="سامانه آموزش هوشمند"
        trailingAction={<AppMenu />}
      />

      <div
        className="flex-1 space-y-4 overflow-x-hidden overflow-y-auto p-4 select-none"
        dir="rtl"
      >
        <div className="space-y-3.5">
          {/* User Greeting Hero Banner */}
          <div className="from-brand-dark to-brand flex items-center justify-between rounded-2xl bg-gradient-to-l p-4 text-white shadow-md">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full border border-white/40 bg-white/20">
                <User className="h-5 w-5 text-white" aria-hidden="true" />
              </div>
              <div>
                <h2 className="text-sm font-bold">
                  خوش آمدید، {user.displayName}
                </h2>
                <p className="text-xs text-white/80">
                  {activeTenant?.tenantName ?? "مدرسه هوشمند کایر"}
                </p>
              </div>
            </div>
          </div>

          {/* Account Details Card */}
          <CardContainer variant="soft" className="space-y-3 p-4">
            <h3 className="text-slate-dark text-xs font-bold">
              مشخصات نشست فعال
            </h3>
            <dl className="grid grid-cols-1 gap-2 text-xs">
              <div className="flex justify-between border-b border-slate-100 pb-1.5">
                <dt className="text-slate-muted">نام کاربری</dt>
                <dd className="font-semibold text-slate-800">
                  {user.username}
                </dd>
              </div>
              <div className="flex justify-between border-b border-slate-100 pb-1.5">
                <dt className="text-slate-muted">شناسه شخص</dt>
                <dd className="font-mono text-[11px] text-slate-600">
                  {user.personId.slice(0, 8)}...
                </dd>
              </div>
              <div className="flex items-center justify-between pt-0.5">
                <dt className="text-slate-muted">نقش‌ها</dt>
                <dd className="flex flex-wrap gap-1">
                  {roles.length > 0 ? (
                    roles.map((r) => (
                      <span
                        key={r}
                        className="bg-brand-subtle text-brand rounded-md px-2 py-0.5 text-[11px] font-bold"
                      >
                        {roleLabel(r, activeTenant?.adminTitle)}
                      </span>
                    ))
                  ) : (
                    <span className="text-slate-muted text-xs">نامشخص</span>
                  )}
                </dd>
              </div>
            </dl>
          </CardContainer>

          {/* Role Capability Verification Card */}
          <CardContainer variant="soft" className="space-y-2.5 p-4">
            <div className="flex items-center gap-2 text-xs font-bold text-slate-700">
              <ShieldCheck className="text-brand h-4 w-4" aria-hidden="true" />
              <span>مجوزهای دسترسی سامانه</span>
            </div>
            <ul className="space-y-2 text-xs">
              <li className="flex items-center justify-between rounded-lg bg-slate-50 p-2">
                <span>مدیریت کلاس آنلاین (معلم):</span>
                <span
                  className={`font-bold ${isTeacher ? "text-emerald-600" : "text-slate-400"}`}
                >
                  {isTeacher ? "مجاز" : "غیرمجاز"}
                </span>
              </li>
              <li className="flex items-center justify-between rounded-lg bg-slate-50 p-2">
                <span>شرکت در کلاس (دانش‌آموز):</span>
                <span
                  className={`font-bold ${isStudent ? "text-emerald-600" : "text-slate-400"}`}
                >
                  {isStudent ? "مجاز" : "غیرمجاز"}
                </span>
              </li>
            </ul>
          </CardContainer>

          {/* Action Navigation Buttons */}
          <div className="space-y-2 pt-1">
            {isTeacher && (
              <Button
                asChild
                variant="brand"
                size="touch"
                className="w-full text-sm font-bold shadow-xs"
              >
                <Link to="/teacher/live-classes">
                  <Video className="ml-2 h-4 w-4" aria-hidden="true" />
                  ورود به کلاس‌های آنلاین من
                </Link>
              </Button>
            )}
            <Button
              asChild
              variant="outlineBrand"
              size="touch"
              className="w-full text-xs font-bold"
            >
              <Link to="/students">
                مشاهده کارنامه و دانش‌آموزان (مسیر محافظت‌شده)
              </Link>
            </Button>
          </div>
        </div>

        {/* Branded Footer */}
        <footer className="text-brand/70 pt-2 pb-2 text-center text-[10px] font-semibold tracking-wider">
          K A Y E R S O F T • نسخه {env.VITE_APP_VERSION}
        </footer>
      </div>
    </ResponsiveViewWrapper>
  );
}
